import {
  Box,
  Button,
  Collapse,
  Flex,
  SimpleGrid,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { DateTime } from "luxon";
import { FaChevronDown, FaChevronUp, FaFileInvoiceDollar, FaFilePdf } from "react-icons/fa";
import { DuimpDetails } from "../../../models/Process";
import { crm, Pill } from "../../../theme";
import SectionCard from "./SectionCard";
import {
  brl,
  cargoControlLabel,
  channelTone,
  duimpSituation,
  inspectionSituation,
  licensingSituation,
  lpcoSituation,
  money,
  toneColors,
} from "./duimp";
import { downloadDuimpStatement } from "../../../services/siscomexService";
import { formatNcm, TableCard, tdProps, thProps } from "./tableStyles";

// Tipos de tributo da API DUIMP (tributosCalculados[].tipo)
const TAX_LABELS: Record<string, string> = {
  II: "Imposto de Importação (II)",
  IPI: "IPI",
  PIS: "PIS-Importação",
  COFINS: "COFINS-Importação",
  TAXA_UTILIZACAO: "Taxa Siscomex",
  ANTIDUMPING: "Direito antidumping",
  MEDIDA_COMPENSATORIA: "Medida compensatória",
  MEDIDA_SALVAGUARDA: "Medida de salvaguarda",
};

const num = (value?: number | null, maxDigits = 5) =>
  value == null ? "—" : value.toLocaleString("pt-BR", { maximumFractionDigits: maxDigits });

const kg = (value?: number | null) =>
  value == null ? "—" : value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });


const formatDate = (value?: string) => {
  const date = value ? DateTime.fromISO(value) : undefined;
  return date?.isValid ? date.toFormat("dd/LL/yyyy") : "—";
};

const Stat = ({
  label,
  value,
  hint,
  highlight,
}: {
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
}) => (
  <Box
    p="14px 16px"
    borderRadius={crm.radiusSm}
    border={`1px solid ${highlight ? "#c7d7fe" : crm.border}`}
    bg={highlight ? crm.brandSoft : crm.subtle}
  >
    <Text fontSize="12px" fontWeight="600" color={crm.textMuted}>
      {label}
    </Text>
    <Text fontSize="20px" fontWeight="700" color={highlight ? crm.brandDark : crm.text} mt="4px" lineHeight="1.2">
      {value}
    </Text>
    {hint && (
      <Text fontSize="12px" color={crm.textFaint} mt="2px">
        {hint}
      </Text>
    )}
  </Box>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="13px" fontWeight="700" color={crm.text} mb="10px">
    {children}
  </Text>
);

/** Dados oficiais da DUIMP, buscados pelo robô do Portal Único (somente leitura). */
const DuimpPanel = ({
  processId,
  details,
  situation,
  channel,
}: {
  processId: string;
  details: DuimpDetails;
  situation?: string;
  channel?: string;
}) => {
  const itemsToggle = useDisclosure();
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const compliance = details.compliance;
  const licensing = licensingSituation(compliance?.licensingSituation);
  const inspections = [
    ...(compliance?.customsInspections ?? []).map((i) => ({ ...i, kind: "Aduaneira" })),
    ...(compliance?.agencyInspections ?? []).map((i) => ({ ...i, kind: "Anuente" })),
  ];

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      await downloadDuimpStatement(processId);
    } catch {
      toast({ title: "Não foi possível gerar o extrato", status: "error" });
    } finally {
      setDownloading(false);
    }
  };
  const status = duimpSituation(situation);
  const totalDue = details.taxes.reduce((sum, t) => sum + (t.due ?? 0), 0);
  const totalPaid = details.taxes.reduce((sum, t) => sum + (t.paid ?? 0), 0);
  const totalWeight = details.items.reduce((sum, i) => sum + (i.netWeightKg ?? 0), 0);
  // Descrição complementar costuma vir vazia na DUIMP: some a coluna em vez de repetir "sem descrição"
  const hasDescriptions = details.items.some((i) => i.description);
  const fetchedAt = DateTime.fromISO(details.fetchedAt);

  return (
    <SectionCard
      icon={<FaFileInvoiceDollar />}
      title="Dados da DUIMP"
      subtitle={
        <>
          Portal Único Siscomex · versão {details.version}
          {fetchedAt.isValid && ` · atualizado em ${fetchedAt.toFormat("dd/LL/yyyy 'às' HH:mm")}`}
        </>
      }
      actions={
        <>
          {channel && (
            <Pill {...toneColors[channelTone(channel)]}>Canal {channel.toLowerCase()}</Pill>
          )}
          {status && <Pill {...toneColors[status.tone]}>{status.label}</Pill>}
          <Button
            size="sm"
            variant="outline"
            borderColor={crm.border}
            color={crm.text}
            leftIcon={<FaFilePdf />}
            isLoading={downloading}
            onClick={downloadStatement}
          >
            Extrato PDF
          </Button>
        </>
      }
    >
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px">
        <Stat
          label="Mercadoria (local de embarque)"
          value={money(details.goodsValueUsd, "USD")}
          hint={details.goodsValueBrl != null ? brl(details.goodsValueBrl) : undefined}
        />
        <Stat
          label="Frete internacional"
          value={money(details.freightValue, details.freightCurrency)}
          hint={details.freightBrl != null && details.freightCurrency !== "BRL" ? brl(details.freightBrl) : undefined}
        />
        <Stat
          label="Seguro"
          value={money(details.insuranceValue, details.insuranceCurrency)}
          hint={details.insuranceBrl != null && details.insuranceCurrency !== "BRL" ? brl(details.insuranceBrl) : undefined}
        />
        <Stat label="Valor aduaneiro (CIF)" value={brl(details.cifBrl)} hint="Mercadoria + frete + seguro" highlight />
      </SimpleGrid>

      {compliance && (
        <Box mt="24px">
          <SectionTitle>Licenciamento e conferência</SectionTitle>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="12px" mb={inspections.length || compliance.lpcos.length ? "12px" : 0}>
            <Stat label="Licenciamento" value={licensing?.label ?? "—"} />
            <Stat label="Controle de carga" value={cargoControlLabel(compliance.cargoControl) ?? "—"} />
            <Stat label="Identificação da carga" value={compliance.cargoIdentification ?? "—"} />
          </SimpleGrid>
          {inspections.length > 0 && (
            <Box mb={compliance.lpcos.length ? "12px" : 0}>
              <TableCard>
                <Thead>
                  <Tr>
                    <Th {...thProps}>Conferência</Th>
                    <Th {...thProps}>Órgão</Th>
                    <Th {...thProps}>Situação</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {inspections.map((inspection, index) => {
                    const state = inspectionSituation(inspection.situation);
                    return (
                      <Tr key={`${inspection.kind}-${inspection.agency}-${index}`} _hover={{ bg: crm.brandSoft }}>
                        <Td {...tdProps}>{inspection.kind}</Td>
                        <Td {...tdProps} fontWeight="600">{inspection.agency ?? "—"}</Td>
                        <Td {...tdProps}><Pill {...toneColors[state.tone]}>{state.label}</Pill></Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </TableCard>
            </Box>
          )}
          {compliance.lpcos.length > 0 && (
            <TableCard>
              <Thead>
                <Tr>
                  <Th {...thProps}>LPCO</Th>
                  <Th {...thProps}>Órgão</Th>
                  <Th {...thProps}>Situação</Th>
                  <Th {...thProps}>Desde</Th>
                  <Th {...thProps}>Vigência até</Th>
                </Tr>
              </Thead>
              <Tbody>
                {compliance.lpcos.map((lpco) => {
                  const state = lpcoSituation(lpco.situation);
                  return (
                    <Tr key={lpco.number} _hover={{ bg: crm.brandSoft }}>
                      <Td {...tdProps} fontWeight="600">{lpco.number}</Td>
                      <Td {...tdProps}>{lpco.agency ?? "—"}</Td>
                      <Td {...tdProps}><Pill {...toneColors[state.tone]}>{state.label}</Pill></Td>
                      <Td {...tdProps}>{formatDate(lpco.situationDate)}</Td>
                      <Td {...tdProps}>{formatDate(lpco.validUntil)}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </TableCard>
          )}
        </Box>
      )}

      {details.taxes.length > 0 && (
        <Box mt="24px">
          <SectionTitle>Tributos</SectionTitle>
          <TableCard>
            <Thead>
              <Tr>
                <Th {...thProps}>Tributo</Th>
                <Th {...thProps} isNumeric>Devido</Th>
                <Th {...thProps} isNumeric>Recolhido</Th>
                <Th {...thProps}>Pagamento</Th>
              </Tr>
            </Thead>
            <Tbody>
              {details.taxes.map((tax) => {
                const paid = (tax.paid ?? 0) > 0;
                return (
                  <Tr key={tax.type} _hover={{ bg: crm.brandSoft }}>
                    <Td {...tdProps} fontWeight="600">{TAX_LABELS[tax.type] ?? tax.type}</Td>
                    <Td {...tdProps} isNumeric>{brl(tax.due)}</Td>
                    <Td {...tdProps} isNumeric>{brl(tax.paid)}</Td>
                    <Td {...tdProps}>
                      {paid ? (
                        <Pill {...toneColors.success}>
                          Pago{tax.paidAt ? ` em ${DateTime.fromISO(tax.paidAt).toFormat("dd/LL/yyyy")}` : ""}
                        </Pill>
                      ) : (
                        <Pill {...toneColors.warning}>Pendente</Pill>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
            <Tfoot>
              <Tr>
                <Td {...tdProps} fontWeight="700" bg={crm.subtle}>Total</Td>
                <Td {...tdProps} fontWeight="700" bg={crm.subtle} isNumeric>{brl(totalDue)}</Td>
                <Td {...tdProps} fontWeight="700" bg={crm.subtle} isNumeric>{brl(totalPaid)}</Td>
                <Td {...tdProps} bg={crm.subtle} />
              </Tr>
            </Tfoot>
          </TableCard>
        </Box>
      )}

      {details.items.length > 0 && (
        <Box mt="24px">
          <Flex align="center" justify="space-between" mb={itemsToggle.isOpen ? "10px" : 0}>
            <Text fontSize="13px" fontWeight="700" color={crm.text}>
              Itens declarados{" "}
              <Text as="span" color={crm.textMuted} fontWeight="500">
                ({details.items.length} · {kg(totalWeight)} kg líquidos)
              </Text>
            </Text>
            <Button
              size="sm"
              variant="ghost"
              color={crm.brand}
              _hover={{ bg: crm.brandSoft }}
              rightIcon={itemsToggle.isOpen ? <FaChevronUp /> : <FaChevronDown />}
              onClick={itemsToggle.onToggle}
            >
              {itemsToggle.isOpen ? "Ocultar" : "Ver itens"}
            </Button>
          </Flex>
          <Collapse in={itemsToggle.isOpen} animateOpacity>
            <TableCard>
              <Thead>
                <Tr>
                  <Th {...thProps}>#</Th>
                  <Th {...thProps}>NCM</Th>
                  {hasDescriptions && <Th {...thProps}>Descrição</Th>}
                  <Th {...thProps} isNumeric>Quantidade</Th>
                  <Th {...thProps} isNumeric>Valor unit.</Th>
                  <Th {...thProps} isNumeric>Peso líq. (kg)</Th>
                  <Th {...thProps}>Origem</Th>
                </Tr>
              </Thead>
              <Tbody>
                {details.items.map((item) => (
                  <Tr key={item.number} _hover={{ bg: crm.brandSoft }}>
                    <Td {...tdProps} color={crm.textMuted}>{item.number}</Td>
                    <Td {...tdProps} fontWeight="600">{formatNcm(item.ncm)}</Td>
                    {hasDescriptions && (
                      <Td {...tdProps} maxW="360px" whiteSpace="normal" color={item.description ? crm.text : crm.textFaint}>
                        {item.description || "—"}
                      </Td>
                    )}
                    <Td {...tdProps} isNumeric>
                      {num(item.quantity)}{" "}
                      <Text as="span" color={crm.textMuted} fontSize="12px">
                        {item.unit?.toLowerCase()}
                      </Text>
                    </Td>
                    <Td {...tdProps} isNumeric>{money(item.unitValue, item.currency)}</Td>
                    <Td {...tdProps} isNumeric>{kg(item.netWeightKg)}</Td>
                    <Td {...tdProps}>{item.originCountry ?? "—"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </TableCard>
          </Collapse>
        </Box>
      )}
    </SectionCard>
  );
};

export default DuimpPanel;
